import { useMixerMenuItem, useTunerMenuItem } from '@tunasong/audio-ui'
import { ElectricGuitar, Fretboard, MusicNote, OpenLink } from '@tunasong/icons'
import { useKeyboardMenuItem } from '@tunasong/music-ui'
import type { Entity, Persisted } from '@tunasong/schemas'
import {
  Typography,
  type TypographyProps,
  useLocation,
  useNavigate,
  useUserConfig,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Disclosure,
  DisclosureTrigger,
  DisclosureContent,
} from '@tunasong/ui-lib'
import cn from 'classnames'
import { useId, type FC } from 'react'
import { groupMusicTheoryTools, musicTheoryTools } from './music-theory-tools.js'
import styles from './tools-menu-items.module.css'
import { useStoredOpen } from './use-stored-open.hook.js'

export interface ToolsMenuItemsProps {
  entity?: Persisted<Entity>
  variant?: TypographyProps['variant']
  onClose?: () => void
  /** Collapsed sidebar rail: icon-only rows, and Music theory expands the sidebar instead of opening in place. */
  rail?: boolean
  /** Rail only: Music theory was chosen, so the sidebar should expand to show the opened group. */
  onExpandRequest?: () => void
}

/** Stored under the key the Music theory disclosure has always used, so a reader's choice survives the redesign. */
const theoryGroupId = 'sidebar.music-theory'

const theoryGroups = groupMusicTheoryTools(musicTheoryTools)
const theoryRoutes = new Set(musicTheoryTools.map(tool => tool.route))

/**
 * The Tools navigation list (sidebar, collapsed rail and the mobile Tools dialog). A plain list of buttons,
 * not an ARIA menu: every row is a Tab stop and the current page is announced with aria-current.
 */
export const ToolsMenuItems: FC<ToolsMenuItemsProps> = props => {
  const { onClose, variant, rail = false, onExpandRequest } = props

  const { dialog: mixerDialog, menuItem: mixerMenuItem } = useMixerMenuItem({
    variant,
    onClose,
  })
  const { dialog: tunerDialog, menuItem: tunerMenuItem } = useTunerMenuItem({ variant, onClose })
  const { dialog: keyboardDialog, menuItem: keyboardMenuItem } = useKeyboardMenuItem({
    variant,
    onClose,
  })

  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [externalTools = []] = useUserConfig('externalTools')
  const [theoryOpen, setTheoryOpen] = useStoredOpen(theoryGroupId, false)
  const idBase = useId()

  const isCurrent = (route: string) => pathname === route
  const currentFor = (route: string) => (isCurrent(route) ? 'page' : undefined)

  /* A theory page is open but its row is hidden (group closed, or the rail): the group row stands in for it. */
  const theoryStandsInForCurrent = (rail || !theoryOpen) && theoryRoutes.has(pathname)

  const handleRailTheory = () => {
    setTheoryOpen(true)
    onExpandRequest?.()
  }

  const toolRow = (label: string, route: string, icon: FC) => {
    const Icon = icon
    return (
      <li>
        <ListItemButton
          className={cn(isCurrent(route) && styles.active)}
          aria-current={currentFor(route)}
          title={label}
          onClick={() => void navigate(route)}
        >
          <ListItemIcon className={styles.listItemIcon} aria-hidden="true">
            <Icon />
          </ListItemIcon>
          <ListItemText>
            <Typography variant={variant}>{label}</Typography>
          </ListItemText>
        </ListItemButton>
      </li>
    )
  }

  return (
    <>
      <List disablePadding className={cn(styles.tools, rail && styles.rail)}>
        <li>{mixerMenuItem}</li>
        <li>{tunerMenuItem}</li>
        <li>{keyboardMenuItem}</li>

        {toolRow('Guitar chords', '/app/guitarchords', ElectricGuitar)}
        {toolRow('Guitar patterns', '/app/guitarpatterns', Fretboard)}

        {rail ? (
          <li>
            <ListItemButton
              className={cn(theoryStandsInForCurrent && styles.active)}
              aria-current={theoryStandsInForCurrent ? 'page' : undefined}
              title="Music theory"
              onClick={handleRailTheory}
            >
              <ListItemIcon className={styles.listItemIcon} aria-hidden="true">
                <MusicNote />
              </ListItemIcon>
              <ListItemText>
                <Typography variant={variant}>Music theory</Typography>
              </ListItemText>
            </ListItemButton>
          </li>
        ) : (
          <Disclosure groupId={theoryGroupId} open={theoryOpen} onOpenChange={setTheoryOpen}>
            <DisclosureTrigger
              className={cn(styles.theoryTrigger, theoryStandsInForCurrent && styles.active)}
              aria-current={theoryStandsInForCurrent ? 'page' : undefined}
            >
              <ListItemIcon className={styles.listItemIcon} aria-hidden="true">
                <MusicNote />
              </ListItemIcon>
              <ListItemText>
                <Typography variant={variant}>Music theory</Typography>
              </ListItemText>
              <span className={styles.count}>{musicTheoryTools.length}</span>
            </DisclosureTrigger>
            <DisclosureContent className={styles.tree}>
              {theoryGroups.map(({ group, tools }) => {
                const labelId = `${idBase}-theory-${group}`
                return (
                  <li key={group}>
                    <div role="group" aria-labelledby={labelId}>
                      <span id={labelId} className={styles.subLabel}>
                        {group}
                      </span>
                      <ul className={styles.subList}>
                        {tools.map(({ label, route }) => (
                          <li key={route}>
                            <ListItemButton
                              className={cn(styles.theoryRow, isCurrent(route) && styles.active)}
                              aria-current={currentFor(route)}
                              onClick={() => void navigate(route)}
                            >
                              <ListItemText>
                                <Typography variant={variant}>{label}</Typography>
                              </ListItemText>
                            </ListItemButton>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </li>
                )
              })}
            </DisclosureContent>
          </Disclosure>
        )}

        {externalTools.length > 0 && !rail ? (
          <li className={styles.subLabel}>
            <Typography variant="caption">External tools</Typography>
          </li>
        ) : null}
        {externalTools.map((tool, index) => (
          <li key={index}>
            <ListItemButton
              title={tool.name}
              onClick={() => {
                void navigate(
                  `/app/external-tool?url=${encodeURIComponent(tool.url)}&name=${encodeURIComponent(tool.name)}`
                )
                onClose?.()
              }}
            >
              <ListItemIcon className={styles.listItemIcon} aria-hidden="true">
                <OpenLink />
              </ListItemIcon>
              <ListItemText>
                <Typography variant={variant}>{tool.name}</Typography>
              </ListItemText>
            </ListItemButton>
          </li>
        ))}
      </List>

      {mixerDialog}
      {tunerDialog}
      {keyboardDialog}
    </>
  )
}
